import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { buildHideLoaderHeaders } from '../utils/api-headers.util';
import { map } from 'rxjs/operators';
import { TicketField } from 'src/app/shared/constants/ticket-field.constants';
import {
  CreateTicketRequest,
  CreateTicketResponse,
  TicketFilterRequest,
  TicketGetResponse,
  TicketDetailResponse,
  TicketSearchPageResponse,
  UpdateTicketStatusRequest,
} from 'src/app/shared/models/tickets/ticket.model';

@Injectable({
  providedIn: 'root',
})
export class TicketClientApiService {
  typeEnvironmentApim = environment.Apim;

  constructor(private readonly http: HttpClient) { }

  /**
   * Detalle de un ticket. Trae `solution`/`assignedAdvisor`/`priority`/
   * `closureType`/`metadata`, que `find-all-tickets` no devuelve.
   */
  getTicketDetail(ticketRecordId: string, options?: { silent?: boolean }): Observable<TicketDetailResponse> {
    return this.http.get<TicketDetailResponse>(
      `${this.typeEnvironmentApim.migration_host}/` +
      `${this.typeEnvironmentApim.migration_paths.api}/` +
      `${this.typeEnvironmentApim.migration_paths.tickets}/${ticketRecordId}`,
      options?.silent ? { headers: buildHideLoaderHeaders() } : undefined
    );
  }

  // La respuesta viene como `{ content, page }` y se desenvuelve acá; la metadata de `page`
  // se descarta. Usar `findAllTicketsPaged()` si hace falta.
  findAllTickets(request: TicketFilterRequest, options?: { silent?: boolean }): Observable<TicketGetResponse[]> {
    return this.http.post<TicketSearchPageResponse>(
      `${this.typeEnvironmentApim.migration_host}/` +
      `${this.typeEnvironmentApim.migration_paths.api}/` +
      `${this.typeEnvironmentApim.migration_paths.tickets}/` +
      `${this.typeEnvironmentApim.migration_paths.ticketsFindAll}`,
      request,
      options?.silent ? { headers: buildHideLoaderHeaders() } : undefined
    ).pipe(
      map(res => res?.content ?? []),
    );
  }

  // Misma búsqueda que `findAllTickets()`, pero expone `{content, page}`
  // completo — para "ver más" real (necesita `hasNext`/`totalElements`).
  findAllTicketsPaged(request: TicketFilterRequest, options?: { silent?: boolean }): Observable<TicketSearchPageResponse> {
    return this.http.post<TicketSearchPageResponse>(
      `${this.typeEnvironmentApim.migration_host}/` +
      `${this.typeEnvironmentApim.migration_paths.api}/` +
      `${this.typeEnvironmentApim.migration_paths.tickets}/` +
      `${this.typeEnvironmentApim.migration_paths.ticketsFindAll}`,
      request,
      options?.silent ? { headers: buildHideLoaderHeaders() } : undefined
    );
  }

  // Cambio de estado de un ticket. Endpoint propio, distinto al
  // update-string-field que usan las constituciones.
  updateTicketStatus(recordId: string, request: UpdateTicketStatusRequest): Observable<any> {
    return this.http.patch<any>(
      `${this.typeEnvironmentApim.migration_host}/` +
      `${this.typeEnvironmentApim.migration_paths.api}/` +
      `${this.typeEnvironmentApim.migration_paths.tickets}/${recordId}/` +
      `${this.typeEnvironmentApim.migration_paths.ticketsStatus}`,
      request
    );
  }

  // Alta de ticket contra el orquestador de migración. La categoría decide en
  // qué tablero aparece; los adjuntos se suben aparte, por el recurso document.
  createTicket(request: CreateTicketRequest): Observable<CreateTicketResponse> {
    return this.http.post<CreateTicketResponse>(
      `${this.typeEnvironmentApim.migration_host}/` +
      `${this.typeEnvironmentApim.migration_paths.api}/` +
      `${this.typeEnvironmentApim.migration_paths.tickets}/` +
      `${this.typeEnvironmentApim.migration_paths.ticketsCreate}`,
      request
    );
  }

  /**
   * Único endpoint de edición de campos de ticket. `newValue` es libre: el
   * contrato acepta string, arreglo (`incidentTypes`), objeto (`metadata`) o null.
   */
  updateTicketField(recordId: string, fieldName: TicketField, newValue: unknown): Observable<any> {
    return this.http.patch<any>(
      `${this.typeEnvironmentApim.migration_host}/` +
      `${this.typeEnvironmentApim.migration_paths.api}/` +
      `${this.typeEnvironmentApim.migration_paths.updateFieldTickets}/${recordId}`,
      { fieldName, newValue }
    );
  }
}
